import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  IconButton,
  TextField,
  Typography
} from '@mui/material';

import {
  CheckOutlined,
  ChevronLeftOutlined,
  ClearOutlined,
  DeleteOutlined
} from '@mui/icons-material';

import {
  panelSx,
  panelHeaderSx,
  panelTitleSx,
  addRowSx,
  diagnosisListSx,
  diagnosisRowSx,
  diagnosisTextSx,
  iconButtonSx,
  moveButtonSx
} from '../Styles';

const SystemDiagnosisPanel = ({
  diagnoses,
  onMove,
  onAddText,
  onDelete
}) => {
  const [newText, setNewText] =
    useState('');

  const handleAdd = () => {
    const value =
      newText.trim();

    if (!value) {
      return;
    }

    onAddText(value);
    setNewText('');
  };

  const handleClear = () => {
    setNewText('');
  };

  return (
    <Box sx={panelSx}>
      <Box sx={panelHeaderSx}>
        <Typography
          sx={panelTitleSx}
        >
          System ICD & Descriptions
        </Typography>
      </Box>

      <Box sx={addRowSx}>
        <TextField
          fullWidth
          size="small"
          placeholder="Add new text"
          value={newText}
          onChange={event =>
            setNewText(
              event.target.value
            )
          }
          onKeyDown={event => {
            if (
              event.key === 'Enter'
            ) {
              event.preventDefault();
              handleAdd();
            }

            if (
              event.key === 'Escape'
            ) {
              handleClear();
            }
          }}
        />

        <IconButton
          size="small"
          disabled={!newText.trim()}
          onClick={handleAdd}
          sx={{
            ...iconButtonSx,
            color: '#4CAF50'
          }}
        >
          <CheckOutlined
            sx={{
              fontSize: 18
            }}
          />
        </IconButton>

        <IconButton
          size="small"
          disabled={!newText}
          onClick={handleClear}
          sx={{
            ...iconButtonSx,
            color: '#EF5350'
          }}
        >
          <ClearOutlined
            sx={{
              fontSize: 18
            }}
          />
        </IconButton>
      </Box>

      <Box sx={diagnosisListSx}>
        {diagnoses.length > 0 ? (
          diagnoses.map(diagnosis => (
            <Box
              key={diagnosis.id}
              sx={diagnosisRowSx}
            >
              <IconButton
                size="small"
                aria-label={`Move ${diagnosis.description}`}
                onClick={() =>
                  onMove(diagnosis)
                }
                sx={moveButtonSx}
              >
                <ChevronLeftOutlined
                  sx={{
                    fontSize: 15
                  }}
                />
              </IconButton>

              <Typography
                title={
                  diagnosis.code
                    ? `${diagnosis.code} ${diagnosis.description}`
                    : diagnosis.description
                }
                sx={diagnosisTextSx}
              >
                {diagnosis.code && (
                  <>
                    {diagnosis.code}
                    {' '}
                  </>
                )}

                {diagnosis.description}
              </Typography>

              <IconButton
                size="small"
                aria-label="Delete diagnosis"
                onClick={() =>
                  onDelete(
                    diagnosis.id
                  )
                }
                sx={iconButtonSx}
              >
                <DeleteOutlined
                  sx={{
                    fontSize: 17
                  }}
                />
              </IconButton>
            </Box>
          ))
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                color: '#757575'
              }}
            >
              No system ICD
              descriptions available
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

SystemDiagnosisPanel.propTypes = {
  diagnoses:
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([
          PropTypes.number,
          PropTypes.string
        ]).isRequired,
        code:
          PropTypes.string,
        description:
          PropTypes.string.isRequired
      })
    ),
  onMove:
    PropTypes.func,
  onAddText:
    PropTypes.func,
  onDelete:
    PropTypes.func
};

SystemDiagnosisPanel.defaultProps = {
  diagnoses: [],
  onMove: () => {},
  onAddText: () => {},
  onDelete: () => {}
};

export default SystemDiagnosisPanel;